import type { BuckerarrestorParsedFile } from './buckerarrestorWeldingTxtParser';
import type { TlogExcelRow } from './UploadTlogExcelDataDialog';

export interface BuckerarrestorToTlogOptions {
  projectId: number;
  weldId: string;
  jobNumber?: string;
}

/** One preview row per Buckerarrestor pass file — summary Min/Avg/Max for UI. */
export interface BuckerarrestorPassPreviewRow {
  weldNumber: string;
  jobNumber: string;
  passName: string;
  /** File basename without .txt — matches pass_view.weld_uid after S/T/C upload. */
  weldUid: string;
  /** SUMMARY (user/machine) Min/Avg/Max. */
  voltsMin: string;
  voltsAvg: string;
  voltsMax: string;
  currentMin: string;
  currentAvg: string;
  currentMax: string;
  /** Mean/min/max from T-log sample rows (auto-calc). Volts 1 dp; current 0 dp. */
  voltsMinCalc: string;
  voltsAvgCalc: string;
  voltsMaxCalc: string;
  currentMinCalc: string;
  currentAvgCalc: string;
  currentMaxCalc: string;
  travelMin: string;
  travelAvg: string;
  travelMax: string;
  /** KJ/inch from Buckerarrestor SUMMARY line 83 (single value per pass). */
  heatInput: string;
  /** Arc duration seconds from SUMMARY — feeds weld_status Duration. */
  durationSeconds: number | null;
  /** Pass start unix seconds — pass/zone/tilt s_time (same as status-calc). */
  startUnixSeconds: number | null;
  /** Pass end unix seconds — pass c_time / zone·tilt s_c_time. */
  endUnixSeconds: number | null;
}

function preserveDecimalString(value: unknown, defaultValue = ''): string {
  if (value == null || value === '') return defaultValue;
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed) return defaultValue;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return trimmed;
    return parseFloat(n.toFixed(10)).toString();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return parseFloat(value.toFixed(10)).toString();
  }
  return String(value ?? defaultValue).trim() || defaultValue;
}

function baseDateFromFileName(
  fileName: string
): { year: number; month: number; day: number } | null {
  const dateMatch = fileName.match(/(\d{8})/);
  if (!dateMatch) return null;
  const ymd = dateMatch[1];
  const year = parseInt(ymd.slice(0, 4), 10);
  const month = parseInt(ymd.slice(4, 6), 10) - 1;
  const day = parseInt(ymd.slice(6, 8), 10);
  if (year >= 1900 && year <= 2100 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
    return { year, month, day };
  }
  return null;
}

function timeOfDayToUnix(
  timeStr: string | null | undefined,
  baseDate: { year: number; month: number; day: number } | null
): number {
  if (!timeStr) return 0;
  const [h, m, sec] = String(timeStr).split(/[:\s]/).map(Number);
  if (!(h >= 0 && h <= 23 && m >= 0 && m <= 59)) return 0;
  const date = baseDate
    ? new Date(
        baseDate.year,
        baseDate.month,
        baseDate.day,
        h,
        Number.isNaN(m) ? 0 : m,
        Number.isNaN(sec) ? 0 : sec,
        0
      )
    : (() => {
        const d = new Date();
        d.setHours(h, Number.isNaN(m) ? 0 : m, Number.isNaN(sec) ? 0 : sec, 0);
        return d;
      })();
  return Math.floor(date.getTime() / 1000);
}

function travelMinAvgMax(file: BuckerarrestorParsedFile): { min: string; avg: string; max: string } {
  const speeds = file.sampleRows
    .map((s) => s.travelSpeed)
    .filter((v): v is number => v != null && Number.isFinite(v));
  if (speeds.length) {
    const min = Math.min(...speeds);
    const max = Math.max(...speeds);
    const avg = speeds.reduce((a, b) => a + b, 0) / speeds.length;
    return {
      min: preserveDecimalString(min),
      avg: preserveDecimalString(parseFloat(avg.toFixed(4))),
      max: preserveDecimalString(max),
    };
  }
  const single = preserveDecimalString(file.travelSpeed);
  return { min: single, avg: single, max: single };
}

/** Preview display rounding: volts 1 decimal, current whole amps. */
function roundMetricString(value: unknown, decimals: number): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number(String(value).trim().replace(/,/g, ''));
  if (!Number.isFinite(n)) return preserveDecimalString(value);
  return preserveDecimalString(parseFloat(n.toFixed(decimals)));
}

function sampleMinAvgMax(
  values: number[],
  decimals: number
): { min: string; avg: string; max: string } {
  if (!values.length) return { min: '', avg: '', max: '' };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  return {
    min: roundMetricString(min, decimals),
    avg: roundMetricString(avg, decimals),
    max: roundMetricString(max, decimals),
  };
}

function sampleVoltsCurrentStats(file: BuckerarrestorParsedFile): {
  volts: { min: string; avg: string; max: string };
  current: { min: string; avg: string; max: string };
} {
  const volts = file.sampleRows
    .map((s) => s.voltage)
    .filter((v): v is number => v != null && Number.isFinite(v));
  const currents = file.sampleRows
    .map((s) => s.current)
    .filter((v): v is number => v != null && Number.isFinite(v));
  return {
    volts: sampleMinAvgMax(volts, 1),
    current: sampleMinAvgMax(currents, 0),
  };
}

/**
 * Pass-level preview: Volts / Current / Travel Min·Avg·Max + Heat (from Buckerarrestor SUMMARY).
 */
export function buildBuckerarrestorPassPreviewRows(
  files: BuckerarrestorParsedFile[],
  options: Pick<BuckerarrestorToTlogOptions, 'weldId' | 'jobNumber'>
): BuckerarrestorPassPreviewRow[] {
  const weldId = String(options.weldId || '').trim();
  const jobNumber = String(options.jobNumber || 'JOB-1').trim() || 'JOB-1';
  return files.map((file) => {
    const travel = travelMinAvgMax(file);
    const sampleStats = sampleVoltsCurrentStats(file);
    const baseDate = baseDateFromFileName(file.fileName);
    const startUnix = timeOfDayToUnix(file.startTime, baseDate);
    let endUnix = timeOfDayToUnix(file.finishTime, baseDate);
    const durationSec =
      file.arcTimeSeconds != null && Number.isFinite(file.arcTimeSeconds)
        ? file.arcTimeSeconds
        : null;
    if (!(endUnix > 0) && startUnix > 0 && durationSec != null && durationSec > 0) {
      endUnix = startUnix + Math.round(durationSec);
    }
    const resolvedStart =
      startUnix > 0
        ? startUnix
        : endUnix > 0 && durationSec != null && durationSec > 0
          ? endUnix - Math.round(durationSec)
          : 0;
    return {
      weldNumber: weldId,
      jobNumber,
      passName: file.passName || 'UNKNOWN',
      weldUid: String(file.fileName || '').replace(/\.txt$/i, ''),
      voltsMin: roundMetricString(file.voltageMin, 1),
      voltsAvg: roundMetricString(file.voltageAvg, 1),
      voltsMax: roundMetricString(file.voltageMax, 1),
      currentMin: roundMetricString(file.currentMin, 0),
      currentAvg: roundMetricString(file.currentAvg, 0),
      currentMax: roundMetricString(file.currentMax, 0),
      voltsMinCalc: sampleStats.volts.min,
      voltsAvgCalc: sampleStats.volts.avg,
      voltsMaxCalc: sampleStats.volts.max,
      currentMinCalc: sampleStats.current.min,
      currentAvgCalc: sampleStats.current.avg,
      currentMaxCalc: sampleStats.current.max,
      heatInput: preserveDecimalString(file.heatInput),
      durationSeconds: durationSec,
      startUnixSeconds: resolvedStart > 0 ? resolvedStart : null,
      endUnixSeconds: endUnix > 0 ? endUnix : null,
      travelMin: travel.min,
      travelAvg: travel.avg,
      travelMax: travel.max,
    };
  });
}

/**
 * Map parsed Buckerarrestor files → intermediate rows consumed by parseTlogExcelToStcRecords.
 */
export function buckerarrestorParsedFilesToTlogExcelRows(
  files: BuckerarrestorParsedFile[],
  options: BuckerarrestorToTlogOptions
): TlogExcelRow[] {
  const weldId = String(options.weldId || '').trim();
  const jobNumber = String(options.jobNumber || 'JOB-1').trim() || 'JOB-1';
  const projectId = options.projectId || 0;
  const rows: TlogExcelRow[] = [];
  let event = 1;

  /** Stable file order → stable S Event / welduid on re-upload. */
  const orderedFiles = [...files].sort((a, b) =>
    String(a.fileName || '').localeCompare(String(b.fileName || ''))
  );

  for (const file of orderedFiles) {
    const baseName = file.fileName.replace(/\.txt$/i, '');
    const baseDate = baseDateFromFileName(file.fileName);
    const passName = file.passName || 'UNKNOWN';
    const durationSec = file.arcTimeSeconds ?? 0;
    const samples = file.sampleRows.length
      ? file.sampleRows
      : [
          {
            voltage: file.voltageAvg ?? 0,
            current: file.currentAvg ?? 0,
            recordDuration: 0,
            startTime: file.startTime,
            finishTime: file.finishTime,
            travelSpeed: file.travelSpeed,
          },
        ];

    for (const sample of samples) {
      const sUnix = timeOfDayToUnix(sample.startTime || file.startTime, baseDate);
      const eUnix = timeOfDayToUnix(sample.finishTime || file.finishTime, baseDate);
      rows.push({
        project_id: projectId,
        welduid_s_event_torch: baseName,
        weld_number: weldId,
        zone_name: passName,
        time: eUnix || sUnix,
        job_number: jobNumber,
        travel_speed: preserveDecimalString(sample.travelSpeed ?? file.travelSpeed),
        current: preserveDecimalString(sample.current),
        volts: preserveDecimalString(sample.voltage),
        heat: preserveDecimalString(file.heatInput),
        s_time: sUnix,
        e_time: eUnix || sUnix,
        duration: durationSec,
        event: event++,
        current_min: preserveDecimalString(file.currentMin),
        current_avg: preserveDecimalString(file.currentAvg),
        current_max: preserveDecimalString(file.currentMax),
        voltage_min: preserveDecimalString(file.voltageMin),
        voltage_avg: preserveDecimalString(file.voltageAvg),
        voltage_max: preserveDecimalString(file.voltageMax),
      });
    }
  }

  return rows;
}
